import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Building2, Zap, Users, TrendingUp, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const LoginPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [nom, setNom] = useState('');
  const [prenom, setPrenom] = useState('');
  const [telephone, setTelephone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (isLogin) {
        const user = await login(email, password);
        toast.success(`Bienvenue, ${user.prenom} !`);
        // Redirect based on role
        if (user.role === 'super_admin') {
          navigate('/super-admin');
        } else if (user.role === 'admin') {
          navigate('/admin');
        } else {
          navigate('/agent');
        }
      } else {
        const user = await register({
          email,
          password,
          nom,
          prenom,
          telephone,
          role: 'admin' // First user is admin
        });
        toast.success('Compte créé avec succès !');
        navigate('/admin');
      }
    } catch (err) {
      const message = err.response?.data?.detail || 'Une erreur est survenue';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      {/* Left side - Branding */}
      <div className="login-left hidden lg:flex">
        <div className="max-w-md text-center">
          <div className="flex items-center justify-center gap-3 mb-8">
            <div className="w-14 h-14 bg-white/10 rounded-xl flex items-center justify-center">
              <Building2 className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold">CoproEnergy</h1>
          </div>
          
          <p className="text-xl text-blue-100 mb-12">
            Plateforme de gestion commerciale pour la rénovation énergétique en copropriété
          </p>

          <div className="space-y-6 text-left">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <Zap className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-semibold mb-1">Qualification DPE</h3>
                <p className="text-sm text-blue-200">Identifiez les copropriétés à fort potentiel énergétique</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <Users className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-semibold mb-1">Gestion des secteurs</h3>
                <p className="text-sm text-blue-200">Attribuez les territoires à vos agents commerciaux</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <TrendingUp className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-semibold mb-1">Pipeline commercial</h3>
                <p className="text-sm text-blue-200">Suivez vos opportunités de la prospection à la signature</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="login-right w-full lg:w-1/2">
        <Card className="w-full max-w-md border-0 shadow-none lg:border lg:shadow-sm">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center gap-2 mb-4 lg:hidden">
              <Building2 className="w-8 h-8 text-[#1E3A8A]" />
              <span className="text-2xl font-bold text-[#0F172A]">CoproEnergy</span>
            </div>
            <CardTitle className="text-2xl font-bold text-[#0F172A]">
              {isLogin ? 'Connexion' : 'Créer un compte'}
            </CardTitle>
            <CardDescription className="text-[#64748B]">
              {isLogin 
                ? 'Accédez à votre espace de gestion' 
                : 'Créez votre compte administrateur'}
            </CardDescription>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  {error}
                </div>
              )}

              {!isLogin && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="prenom">Prénom</Label>
                      <Input
                        id="prenom"
                        data-testid="register-prenom-input"
                        value={prenom}
                        onChange={(e) => setPrenom(e.target.value)}
                        required={!isLogin}
                        placeholder="Jean"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="nom">Nom</Label>
                      <Input
                        id="nom"
                        data-testid="register-nom-input"
                        value={nom}
                        onChange={(e) => setNom(e.target.value)}
                        required={!isLogin}
                        placeholder="Dupont"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="telephone">Téléphone</Label>
                    <Input
                      id="telephone"
                      data-testid="register-telephone-input"
                      value={telephone}
                      onChange={(e) => setTelephone(e.target.value)}
                      placeholder="06 12 34 56 78"
                    />
                  </div>
                </>
              )}

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="login-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="jean.dupont@exemple.fr"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Mot de passe</Label>
                  {isLogin && (
                    <Link 
                      to="/forgot-password" 
                      className="text-xs text-[#1E3A8A] hover:underline"
                      data-testid="forgot-password-link"
                    >
                      Mot de passe oublié ?
                    </Link>
                  )}
                </div>
                <Input
                  id="password"
                  type="password"
                  data-testid="login-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="••••••••"
                />
              </div>

              <Button
                type="submit"
                data-testid="login-submit-button"
                className="w-full bg-[#1E3A8A] hover:bg-[#1E40AF]"
                disabled={loading}
              >
                {loading ? 'Chargement...' : (isLogin ? 'Se connecter' : 'Créer le compte')}
              </Button>
            </form>

            <div className="mt-6 text-center space-y-3">
              <button
                type="button"
                data-testid="toggle-auth-mode"
                onClick={() => setIsLogin(!isLogin)}
                className="text-sm text-[#1E3A8A] hover:underline"
              >
                {isLogin 
                  ? "Pas de compte ? Créer un compte administrateur" 
                  : "Déjà un compte ? Se connecter"}
              </button>
              
              {isLogin && (
                <div className="pt-4 border-t">
                  <p className="text-sm text-slate-500 mb-2">Vous êtes une entreprise ?</p>
                  <Link
                    to="/register"
                    className="inline-flex items-center justify-center px-4 py-2 border-2 border-[#1E3A8A] text-[#1E3A8A] rounded-lg font-medium hover:bg-[#1E3A8A] hover:text-white transition-colors"
                  >
                    Souscrire à CoproEnergy
                  </Link>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LoginPage;
